import { Show, createEffect, createMemo, createSignal } from "solid-js";

import { useGame } from "@/hooks/game/useGame";
import { getGamePlatListBySelect } from "@/hooks/useCasino";
import { globalSearch, useGlobalSearch } from "@/hooks/useGlobalSearch";
import { useI18n } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import Select from "@/pages/pc/casino/component/Select";
import { GetMakerListRsp } from "@/types/swagger/front";

type SearchSelectProps = {
  debouncedFetch: (isLoadMore?: boolean) => void;
  gameItemPreviewScrollToTop: () => void;
};

type PlatformItemTypes = GetMakerListRsp["list"][number] & {
  label: string;
  value: string;
};

export const SearchSelect = (props: SearchSelectProps) => {
  const { t } = useI18n();

  const { setSearchCasinoParams } = useGlobalSearch();
  const { gameSortList } = useGame();

  const [platform, setPlatform] = createSignal<PlatformItemTypes[]>([]);

  const gclass = createMemo(() =>
    Number(globalSearch().searchCasinoParams.gclass),
  );

  // 确保 sort 值能够响应状态变化
  const currentSort = createMemo(() => globalSearch().searchCasinoParams.sort);

  // 處理 gclass 改变并触发异步加载平台资料
  createEffect(() => {
    (async () => {
      const currentGclass = gclass();
      const res = await getGamePlatListBySelect(currentGclass);

      const sortedList = res?.sort((a, b) => a.tag.localeCompare(b.tag)) || [];
      setPlatform(sortedList);
    })();
  });

  return (
    <Show when={globalSearch().curMainCategory === "Casino"} fallback={<></>}>
      <div
        class="flex items-start justify-start gap-[10px]"
        classList={{
          "h-[40px]": isMobile(),
          "h-[44px]": !isMobile(),
        }}
      >
        <Select
          inputClass={`rounded-sm !px-[10px]  leading-[16px] ${
            isMobile() ? "h-[40px] text-[13px]" : "h-[44px] text-[14px]"
          }`}
          options={gameSortList()}
          bgColor="bg-general-bg2"
          defaultValue={currentSort()}
          isClickOutsideClose={!isMobile()}
          showSelectedPrefix
          onChange={async (value) => {
            setSearchCasinoParams({
              sort: String(value),
            });
            // 先加载数据，数据加载完成后再跳回顶部
            await props.debouncedFetch(false);
            props.gameItemPreviewScrollToTop();
          }}
        />
        <Select
          inputClass={`rounded-sm !px-[10px]  leading-[16px] ${
            isMobile() ? "h-[40px] text-[13px]" : "h-[44px] text-[14px]"
          }`}
          options={platform()}
          showSearch
          multiple
          searchPlaceholder={t("search.search")}
          bgColor="bg-general-bg2"
          showSelectedPrefix
          selectedPrefixText={
            <>
              <span
                class="font-[500]"
                classList={{
                  " leading-[17px]": isMobile(),
                  " leading-[16px]": !isMobile(),
                }}
              >
                {t("home.providers")}:
              </span>{" "}
              <span
                class="text-general-light font-[600]"
                classList={{
                  " leading-[17px]": isMobile(),
                  " leading-[16px]": !isMobile(),
                }}
              >
                {t("home.all")}
              </span>
            </>
          }
          isClickOutsideClose={!isMobile()}
          onChange={async (value) => {
            const currentParams = globalSearch().searchCasinoParams;
            const makers = currentParams.maker_tag
              ? currentParams.maker_tag.split(",")
              : [];
            if (!makers.includes(value as string)) {
              // 已存在就不添加
              setSearchCasinoParams({
                maker_tag: [value].join(","),
              });
            }
            // 先加载数据，数据加载完成后再跳回顶部
            await props.debouncedFetch(false);
            props.gameItemPreviewScrollToTop();
          }}
        />
      </div>
    </Show>
  );
};
